import type * as React from "react";

import type { QuestionDetails } from "@tycho/web-bindings/types";

export const SmbActionCategories = ["negotiate", "budget", "fundraise", "generic"] as const;
export type SmbActionCategory = (typeof SmbActionCategories)[number];

export type SmbQuestionDetails = QuestionDetails;

/**
 * The only contract every action sees from the list owner. Action-specific
 * props (min/max/etc) are passed in by the corresponding `createXAction`
 * factory instead of through this shape.
 */
export type SmbActionCoreProps = {
  /**
   * Called when the user presses the card's Skip button. If the list owner
   * passes `undefined`, the Skip button renders disabled.
   */
  onSkip?: () => void;
  /**
   * Called when the action is "done" — a callback-style primary CTA fired, a
   * drawer flow finished, etc. The optional `result` is free-form and lets an
   * action report what the user chose (e.g. a slider value).
   *
   * By default a completed card is dismissed (faded out and removed). To keep
   * the card in the active deck and send it to the back instead — for
   * repeatable actions like sharing — pass a result satisfying
   * {@link RecycleOnComplete} (i.e. `{ recycle: true, ... }`).
   */
  onComplete: (result?: unknown) => void;
};

/**
 * Marker an action's `onComplete` result can carry to request that the deck
 * recycle the card to the back rather than dismiss it. The rest of the result
 * payload is still free-form, so existing fields (e.g. `kind`) can ride along.
 */
export type RecycleOnComplete = { recycle: true };

export function isRecycleOnComplete(result: unknown): result is RecycleOnComplete {
  return (
    typeof result === "object" &&
    result !== null &&
    "recycle" in result &&
    (result as { recycle: unknown }).recycle === true
  );
}

/**
 * Normalized, erased shape the list renderer consumes. Per-action prop types
 * are closed over inside `render`, so the list never has to be generic.
 *
 * `render` returns a ReactElement whose `.type` is a stable module-scope
 * component, which lets the list rebuild without remounting stateful cards.
 * Do not replace `render` with `Component: React.ComponentType<...>` — that
 * pattern remounts on every definition rebuild and wipes local card state.
 */
export type SmbActionDefinition = {
  readonly id: string;
  readonly category: SmbActionCategory;
  readonly canSkip: boolean;
  readonly render: (core: SmbActionCoreProps) => React.ReactNode;
};
